/**
 * Shared pure-geometry helpers for point editing a path outline —
 * extracted for #969 (epic #781) so the piece-outline edit tool and
 * the construction Edit sub-mode never re-implement the same math
 * (the anti-drift rule in docs/draw-tool-architecture.md).
 *
 * Everything here is model-free: functions take pathData strings /
 * live paper items plus a scope and return new geometry. Ownership
 * of commits, per-anchor metadata (smoothAnchors / bezierMemory),
 * and derived-line rules stays with the callers.
 */
import type paper from "paper";
import { computeStraightBendHandles } from "../model/straightBend";
import {
  parseSubpathToSegmentList,
  segmentListToPathData,
} from "../model/constructionPathData";
import { deleteAnchor } from "./pathDeletion";

/** An anchor address inside a (possibly compound) path. */
export interface OutlineAnchorRef {
  pathIndex: number;
  segmentIndex: number;
}

/** The compound-vs-single unwrap used by every index-based lookup. */
export function resolveSubpaths(item: paper.Item): paper.Path[] {
  const compound = item as paper.CompoundPath;
  return compound.children && compound.children.length > 0
    ? (compound.children as paper.Path[])
    : [item as unknown as paper.Path];
}

/** Re-resolve the live paper.Segment for an anchor ref. The Scene
 *  re-renders on every selection / commit, so callers must never
 *  hold segment references across events — index lookup on the
 *  live item is what keeps a drag alive. */
export function findSegmentAt(
  item: paper.Item,
  ref: OutlineAnchorRef,
): paper.Segment | null {
  const sub = resolveSubpaths(item)[ref.pathIndex];
  return sub?.segments[ref.segmentIndex] ?? null;
}

/** Re-resolve the live paper.Curve for a curve ref. */
export function findCurveAt(
  item: paper.Item,
  ref: { pathIndex: number; curveIndex: number },
): paper.Curve | null {
  const sub = resolveSubpaths(item)[ref.pathIndex];
  return sub?.curves[ref.curveIndex] ?? null;
}

/** Snapshot of a curve's bracketing handles + click info captured at
 *  drag start; `applyCurveBend` re-derives the handles each frame. */
export interface CurveBendSnapshot {
  t: number;
  origHandleOut: { x: number; y: number };
  origHandleIn: { x: number; y: number };
  downPt: { x: number; y: number };
}

/** Bend a curve so it tracks the cursor — the Edit-mode curve drag.
 *  A straight segment's first bend shapes a natural circular arc
 *  through the cursor (#540); an already-curved segment shifts both
 *  bracketing handles by the same vector, solving 3·t·(1-t)·h = Δ so
 *  B(t) lands on the cursor (t clamped away from the endpoints so
 *  the handles stay finite). Mutates the live curve's handles. */
export function applyCurveBend(
  scope: paper.PaperScope,
  curve: paper.Curve,
  snap: CurveBendSnapshot,
  cursor: { x: number; y: number },
): void {
  let newOut: paper.Point;
  let newIn: paper.Point;
  const wasStraight =
    Math.hypot(snap.origHandleOut.x, snap.origHandleOut.y) < 1e-6 &&
    Math.hypot(snap.origHandleIn.x, snap.origHandleIn.y) < 1e-6;
  if (wasStraight) {
    const A = curve.segment1.point;
    const B = curve.segment2.point;
    const arc = computeStraightBendHandles(
      { x: A.x, y: A.y },
      { x: B.x, y: B.y },
      { x: cursor.x, y: cursor.y },
    );
    newOut = arc
      ? new scope.Point(arc.out.x, arc.out.y)
      : new scope.Point(0, 0);
    newIn = arc ? new scope.Point(arc.in.x, arc.in.y) : new scope.Point(0, 0);
  } else {
    const dx = cursor.x - snap.downPt.x;
    const dy = cursor.y - snap.downPt.y;
    const t = Math.min(0.9, Math.max(0.1, snap.t));
    const factor = 1 / (3 * t * (1 - t));
    newOut = new scope.Point(
      snap.origHandleOut.x + dx * factor,
      snap.origHandleOut.y + dy * factor,
    );
    newIn = new scope.Point(
      snap.origHandleIn.x + dx * factor,
      snap.origHandleIn.y + dy * factor,
    );
  }
  curve.segment1.handleOut = newOut;
  curve.segment2.handleIn = newIn;
}

/** When a tangent on `seg` was just changed via `changedSide`,
 *  compute the value the OPPOSITE tangent should take to satisfy
 *  the lock mode at that anchor:
 *   - "smooth": full mirror — `-changed`
 *   - "angle":  collinear opposite, preserving the opposite side's
 *               current magnitude
 *   - null:     no constraint; returns null so the caller skips it.
 *  Shared by the construction and outline edit controllers. */
export function mirroredHandleFor(
  scope: paper.PaperScope,
  seg: paper.Segment,
  changedSide: "in" | "out",
  mode: "angle" | "smooth" | null,
): paper.Point | null {
  if (!mode) return null;
  const changed = changedSide === "in" ? seg.handleIn : seg.handleOut;
  if (mode === "smooth") {
    return new scope.Point(-changed.x, -changed.y);
  }
  const other = changedSide === "in" ? seg.handleOut : seg.handleIn;
  const changedLen = Math.hypot(changed.x, changed.y);
  if (changedLen === 0) return null;
  const otherLen = Math.hypot(other.x, other.y);
  const targetLen = otherLen > 0 ? otherLen : changedLen;
  const dirX = -changed.x / changedLen;
  const dirY = -changed.y / changedLen;
  return new scope.Point(dirX * targetLen, dirY * targetLen);
}

/** Insert an anchor on a curve of `pathData` at bezier parameter `t`
 *  without changing the visible shape (paper `divideAt` — De
 *  Casteljau). Returns the new pathData plus where the anchor
 *  landed, or null for a degenerate target (missing curve, or a
 *  press within a hair of an existing anchor, which would insert a
 *  zero-length curve). Extracted from ConstructionTool.addAnchorPoint
 *  (#778); the construction caller layers its smoothAnchors /
 *  bezierMemory index remap on top of the returned `insertedAt`. */
export function insertAnchorOnPathData(
  scope: paper.PaperScope,
  pathData: string,
  target: { pathIndex: number; curveIndex: number; t: number },
): {
  pathData: string;
  insertedAt: number;
  dividedCurveHadHandles: boolean;
} | null {
  let cp: paper.CompoundPath | null = null;
  try {
    cp = new scope.CompoundPath({ pathData, insert: false });
  } catch {
    return null;
  }
  const sub = resolveSubpaths(cp)[target.pathIndex];
  const curve = sub?.curves[target.curveIndex];
  if (!sub || !curve) {
    cp.remove();
    return null;
  }
  const EPS = 1e-3;
  if (target.t < EPS || target.t > 1 - EPS) {
    cp.remove();
    return null;
  }
  const dividedCurveHadHandles = curve.hasHandles();
  const loc = curve.getLocationAtTime(target.t);
  if (!loc) {
    cp.remove();
    return null;
  }
  try {
    sub.divideAt(loc);
  } catch {
    cp.remove();
    return null;
  }
  const newPathData = cp.pathData;
  cp.remove();
  if (!newPathData) return null;
  return {
    pathData: newPathData,
    insertedAt: target.curveIndex + 1,
    dividedCurveHadHandles,
  };
}

/** Delete one anchor from `pathData`, preserving the subpath's
 *  closed-ness. Refuses (returns null) when the deletion would drop
 *  a closed subpath below `minAnchors` — a closed outline must keep
 *  enclosing an area — or below 2 for an open one. Other subpaths
 *  of a compound path pass through untouched. */
export function deleteOutlineAnchor(
  scope: paper.PaperScope,
  pathData: string,
  ref: OutlineAnchorRef,
  opts: { minAnchors: number },
): string | null {
  const list = parseSubpathToSegmentList(pathData, ref.pathIndex, scope);
  if (!list) return null;
  if (list.segments.length - 1 < opts.minAnchors) return null;
  const results = deleteAnchor(list, ref.segmentIndex);
  if (results.length !== 1) return null;
  const result = results[0]!;
  // A closed outline must stay closed — `deleteAnchor` collapses a
  // sub-minimum closed loop to open, but the guard above prevents
  // ever reaching that branch for closed subpaths with minAnchors ≥ 3.
  if (list.closed && !result.closed) return null;
  return replaceSubpathInPathData(scope, pathData, ref.pathIndex, () =>
    segmentListToPathData(result, scope),
  );
}

/** Toggle an anchor between a sharp corner and a smooth point using
 *  geometry alone (no lock metadata): a cornered anchor gets
 *  catmull-style smooth handles from its neighbours (paper
 *  `segment.smooth()`); an anchor with any handles gets them
 *  zeroed. Returns the new pathData plus a human label for the
 *  commit. */
export function toggleAnchorSmoothness(
  scope: paper.PaperScope,
  pathData: string,
  ref: OutlineAnchorRef,
): { pathData: string; label: "Smooth corner" | "Sharpen corner" } | null {
  let cp: paper.CompoundPath | null = null;
  try {
    cp = new scope.CompoundPath({ pathData, insert: false });
  } catch {
    return null;
  }
  const seg = findSegmentAt(cp, ref);
  if (!seg) {
    cp.remove();
    return null;
  }
  const hadHandles = !seg.handleIn.isZero() || !seg.handleOut.isZero();
  let label: "Smooth corner" | "Sharpen corner";
  if (hadHandles) {
    seg.handleIn = new scope.Point(0, 0);
    seg.handleOut = new scope.Point(0, 0);
    label = "Sharpen corner";
  } else {
    seg.smooth({ type: "catmull-rom", factor: 0.5 });
    label = "Smooth corner";
  }
  const newPathData = cp.pathData;
  cp.remove();
  if (!newPathData) return null;
  return { pathData: newPathData, label };
}

/** Per-anchor lock entry / handle-memory shapes shared by
 *  construction lines and (since the #969 parity work) piece
 *  outlines. Structural — both models' fields satisfy them. */
export interface AnchorLockEntry {
  pathIndex: number;
  segmentIndex: number;
  mode?: "angle" | "smooth";
}
export interface AnchorMemoryEntry {
  pathIndex: number;
  segmentIndex: number;
  handleIn: { x: number; y: number };
  handleOut: { x: number; y: number };
  preLock?: {
    handleIn: { x: number; y: number };
    handleOut: { x: number; y: number };
  };
  preMirror?: {
    handleIn: { x: number; y: number };
    handleOut: { x: number; y: number };
  };
}

/** Remap per-anchor lock + memory metadata after a point insertion
 *  (`insertAnchorOnPathData`): entries at or past the insertion index
 *  shift up by one; when the divided curve was CURVED, the two
 *  anchors bounding it get their handle magnitudes rewritten by the
 *  split, so a "smooth" lock (equal magnitudes) no longer holds —
 *  demote it to "angle" (collinearity survives De Casteljau) and
 *  drop their stale handle snapshots. Extracted verbatim from
 *  ConstructionTool.addAnchorPoint (#778) so the leather Edit-points
 *  tool remaps identically. */
export function remapAnchorMetaAfterInsert<
  L extends AnchorLockEntry,
  M extends AnchorMemoryEntry,
>(
  smoothAnchors: L[] | undefined,
  bezierMemory: M[] | undefined,
  target: {
    pathIndex: number;
    insertedAt: number;
    dividedCurveHadHandles: boolean;
  },
): { smoothAnchors: L[] | undefined; bezierMemory: M[] | undefined } {
  const onDividedCurve = (segmentIndex: number): boolean =>
    segmentIndex === target.insertedAt - 1 ||
    segmentIndex === target.insertedAt;
  const remappedLocks = smoothAnchors?.map((e) => {
    if (e.pathIndex !== target.pathIndex) return e;
    if (e.segmentIndex >= target.insertedAt)
      return { ...e, segmentIndex: e.segmentIndex + 1 };
    if (
      target.dividedCurveHadHandles &&
      onDividedCurve(e.segmentIndex) &&
      (e.mode ?? "smooth") === "smooth"
    )
      return { ...e, mode: "angle" as const };
    return e;
  });
  const keptMemory = bezierMemory?.filter(
    (e) =>
      !(
        target.dividedCurveHadHandles &&
        e.pathIndex === target.pathIndex &&
        onDividedCurve(e.segmentIndex)
      ),
  );
  const remappedMemory = keptMemory?.map((e) =>
    e.pathIndex === target.pathIndex && e.segmentIndex >= target.insertedAt
      ? { ...e, segmentIndex: e.segmentIndex + 1 }
      : e,
  );
  return {
    smoothAnchors: remappedLocks?.length ? remappedLocks : undefined,
    bezierMemory: remappedMemory?.length ? remappedMemory : undefined,
  };
}

/** Remap per-anchor lock + memory metadata after an anchor deletion:
 *  the deleted anchor's entries drop, later entries on the subpath
 *  shift down by one, and the two anchors that bracketed the deleted
 *  one lose their entries too — `deleteAnchor` zeroes the handle each
 *  had facing the gap, so their locks / snapshots no longer hold. */
export function remapAnchorMetaAfterDelete<
  L extends AnchorLockEntry,
  M extends AnchorMemoryEntry,
>(
  smoothAnchors: L[] | undefined,
  bezierMemory: M[] | undefined,
  ref: OutlineAnchorRef,
  opts: { anchorCount: number; closed: boolean },
): { smoothAnchors: L[] | undefined; bezierMemory: M[] | undefined } {
  const n = opts.anchorCount;
  const before = (ref.segmentIndex - 1 + n) % n;
  const after = (ref.segmentIndex + 1) % n;
  const dropsEntry = (segmentIndex: number): boolean => {
    if (segmentIndex === ref.segmentIndex) return true;
    if (opts.closed) return segmentIndex === before || segmentIndex === after;
    // Open path: only real neighbours drop (no wrap-around).
    return (
      (ref.segmentIndex > 0 && segmentIndex === ref.segmentIndex - 1) ||
      (ref.segmentIndex < n - 1 && segmentIndex === ref.segmentIndex + 1)
    );
  };
  const remap = <E extends { pathIndex: number; segmentIndex: number }>(
    entries: E[] | undefined,
  ): E[] | undefined => {
    const kept = entries
      ?.filter(
        (e) => e.pathIndex !== ref.pathIndex || !dropsEntry(e.segmentIndex),
      )
      .map((e) =>
        e.pathIndex === ref.pathIndex && e.segmentIndex > ref.segmentIndex
          ? { ...e, segmentIndex: e.segmentIndex - 1 }
          : e,
      );
    return kept?.length ? kept : undefined;
  };
  return {
    smoothAnchors: remap(smoothAnchors),
    bezierMemory: remap(bezierMemory),
  };
}

/** Rebuild a compound pathData with one subpath's data replaced.
 *  Single-subpath paths take the fast path (the replacement IS the
 *  result). */
function replaceSubpathInPathData(
  scope: paper.PaperScope,
  pathData: string,
  pathIndex: number,
  buildReplacement: () => string,
): string | null {
  let cp: paper.CompoundPath | null = null;
  try {
    cp = new scope.CompoundPath({ pathData, insert: false });
  } catch {
    return null;
  }
  const subs = resolveSubpaths(cp);
  if (!subs[pathIndex]) {
    cp.remove();
    return null;
  }
  if (subs.length === 1) {
    cp.remove();
    return buildReplacement();
  }
  const parts = subs.map((sub, i) =>
    i === pathIndex ? buildReplacement() : sub.pathData,
  );
  cp.remove();
  return parts.join(" ");
}

/** True when every subpath of `pathData` is closed — the invariant
 *  piece / cutout outlines must satisfy after every edit. */
export function allSubpathsClosed(
  scope: paper.PaperScope,
  pathData: string,
): boolean {
  let cp: paper.CompoundPath | null = null;
  try {
    cp = new scope.CompoundPath({ pathData, insert: false });
  } catch {
    return false;
  }
  const closed = resolveSubpaths(cp).every((sub) => !!sub.closed);
  cp.remove();
  return closed;
}
